/**
 * useEntityHeaderActions — THE composition point for per-entity-type record
 * header actions.
 *
 * `RecordBody` calls only this hook; which entity types contribute
 * actions (and how) is knowledge that lives HERE, keeping the record shell
 * entity-agnostic. Adding header actions for a new entity type means
 * writing its `useXxxHeaderActions` hook and composing it below — one file,
 * no `RecordBody` change.
 *
 * Every per-type hook self-gates: for other entity types it short-circuits
 * (no queries, no timers) and returns a stable EMPTY array, so at most one
 * composed hook is non-empty and the merge below is effectively a
 * pass-through. (Rules of hooks force calling all of them unconditionally —
 * self-gating is what keeps that free.)
 */

import { useMemo } from 'react';

import type { ResponsiveAction } from '@/lib/base/buttons';
import type { CrmObject } from '@/types/crm';

import { useEmailThreadHeaderActions } from './useEmailThreadHeaderActions';
import { useMeetingHeaderActions } from './useMeetingHeaderActions';
import { useWebhookHeaderActions } from './useWebhookHeaderActions';

export function useEntityHeaderActions(
  object: CrmObject | null
): ResponsiveAction[] {
  const meetingActions = useMeetingHeaderActions(object);
  const emailThreadActions = useEmailThreadHeaderActions(object);
  const webhookActions = useWebhookHeaderActions(object);

  return useMemo(() => {
    // Self-gating means at most one list is non-empty — return that list's
    // stable reference rather than allocating a merged copy.
    const nonEmpty = [
      meetingActions,
      emailThreadActions,
      webhookActions,
    ].filter(actions => actions.length > 0);
    if (nonEmpty.length === 0) {
      return meetingActions;
    }
    if (nonEmpty.length === 1) {
      return nonEmpty[0];
    }
    return nonEmpty.flat();
  }, [meetingActions, emailThreadActions, webhookActions]);
}
